import { useEffect, useMemo, useRef } from "react";
import { Card, Alert, Spinner, Table, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faTriangleExclamation,
  faCloudArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import CalendarEventsResultRow from "./CalendarEventsResultRow";

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function normalizeResponse(apiResult) {
  // supports:
  // 1) {success:true, content:{items,total,hasMore,page,limit}}
  // 2) {items,total,hasMore,page,limit}
  // 3) {success:true, content:[...]} (fallback)
  const payload = apiResult?.content ?? apiResult;

  if (payload?.items) {
    return {
      items: safeArray(payload.items),
      total: Number(payload.total ?? payload.items?.length ?? 0),
      hasMore: Boolean(payload.hasMore),
      page: Number(payload.page ?? 1),
      limit: Number(payload.limit ?? 20),
    };
  }

  if (Array.isArray(payload)) {
    return {
      items: payload,
      total: payload.length,
      hasMore: false,
      page: 1,
      limit: payload.length,
    };
  }

  return { items: [], total: 0, hasMore: false, page: 1, limit: 20 };
}

export default function CalendarEventsResults({
  phase,
  error,
  data,
  onLoadMore,
  isLoadingMore,
  privilege, // <-- NEW: przekaż privilege, bo menu zależy od edit itp.
  onDeleteRow, // <-- NEW: opcjonalnie
  onEventChanged, // <-- NEW: opcjonalnie
}) {
  const { items, total, hasMore } = useMemo(
    () => normalizeResponse(data),
    [data],
  );
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore) return;
    if (phase !== "success") return;
    if (isLoadingMore) return;

    const el = sentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) onLoadMore?.();
      },
      { root: null, rootMargin: "250px", threshold: 0.01 },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, phase, isLoadingMore, onLoadMore]);

  const loadedCount = items.length;

  return (
    <Card className="mt-4 shadow-sm">
      <Card.Header className="bg-light fw-semibold d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCloudArrowDown} />
          Wyniki
        </div>

        {phase !== "idle" && (
          <div className="d-flex align-items-center gap-2">
            <Badge bg="secondary">
              Załadowano: {loadedCount} / {total}
            </Badge>
            {hasMore ? (
              <Badge bg="info">więcej dostępne</Badge>
            ) : (
              <Badge bg="success">koniec</Badge>
            )}
          </div>
        )}
      </Card.Header>

      <Card.Body>
        {phase === "idle" && (
          <Alert variant="secondary" className="mb-0">
            <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
            Ustaw filtry i kliknij <strong>Pokaż wydarzenia</strong>.
          </Alert>
        )}

        {phase === "loading" && (
          <div className="d-flex align-items-center gap-3">
            <Spinner animation="border" />
            <div>
              <div className="fw-semibold">Pobieranie danych…</div>
              <div className="text-muted small">
                Ładowanie pierwszej strony wyników.
              </div>
            </div>
          </div>
        )}

        {phase === "error" && (
          <Alert variant="danger" className="mb-0">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
            Nie udało się pobrać danych.
            <div
              className="mt-2 small text-muted"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {String(error ?? "Unknown error")}
            </div>
          </Alert>
        )}

        {phase === "empty" && (
          <Alert variant="warning" className="mb-0">
            <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
            Brak wyników dla podanych filtrów.
          </Alert>
        )}

        {phase === "success" && (
          <>
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Data</th>
                    <th style={{ width: 90 }}>Godz.</th>
                    <th>Budynek</th>
                    <th>Notatka</th>
                    <th style={{ width: 220 }}>Typ</th>
                    <th style={{ width: 200 }}>Inspekcja</th>
                    <th style={{ width: 180 }}>Status</th>
                    <th style={{ width: 160 }}>Info</th>
                    <th style={{ width: 72 }} className="text-end">
                      ⋯
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((ev) => (
                    <CalendarEventsResultRow
                      key={ev.id}
                      event={ev}
                      privilege={privilege}
                      onDelete={onDeleteRow}
                      onEventChanged={onEventChanged}
                    />
                  ))}
                </tbody>
              </Table>
            </div>

            <div ref={sentinelRef} />

            {hasMore && (
              <div className="d-flex align-items-center gap-2 mt-3">
                {isLoadingMore ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    <div className="text-muted small">Doładowywanie…</div>
                  </>
                ) : (
                  <div className="text-muted small">
                    Przewiń niżej, aby doładować kolejne.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
